/**
 * Product analytics client — batch events to backend.
 * See docs/product-analytics-events.md
 */
import { api } from '../api/client'

const SESSION_KEY = 'kartin_analytics_session_id'
const SESSION_STARTED_KEY = 'kartin_analytics_session_started_at'
const FLUSH_INTERVAL_MS = 5000
const MAX_BATCH = 20

export const ANALYTICS_SCHEMA_VERSION = 1

export interface AnalyticsEventPayload {
  event_name: string
  session_id?: string
  surface?: string
  entity_type?: string
  entity_id?: string
  properties?: Record<string, unknown>
}

const ROUTE_TO_SURFACE: Record<string, string> = {
  '/home': 'home',
  '/studio': 'studio',
  '/chat': 'chat',
  '/product-cards': 'product_cards',
  '/illustration': 'illustration',
  '/ideogram': 'ideogram_studio',
  '/infographic-editor': 'infographic_editor',
  '/video-content': 'video_content',
  '/ai-avatar': 'ai_avatar',
  '/balance': 'balance',
  '/history': 'history',
  '/library': 'library',
  '/favorites': 'favorites',
  '/ai-assistant': 'ai_assistant',
  '/account': 'account',
  '/profile': 'profile',
  '/support': 'support',
  '/help': 'help',
  '/admin': 'admin',
}

let buffer: AnalyticsEventPayload[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let flushing = false

export function getAnalyticsSessionId(): string {
  if (typeof window === 'undefined') return ''
  let id = localStorage.getItem(SESSION_KEY)
  if (!id) {
    id = crypto.randomUUID?.() ?? `s_${Date.now()}_${Math.random().toString(36).slice(2)}`
    localStorage.setItem(SESSION_KEY, id)
  }
  return id
}

export function getSessionStartedAt(): number {
  if (typeof window === 'undefined') return Date.now()
  const raw = sessionStorage.getItem(SESSION_STARTED_KEY)
  if (raw) return Number(raw)
  const now = Date.now()
  sessionStorage.setItem(SESSION_STARTED_KEY, String(now))
  return now
}

export function routeToSurface(pathname: string): string {
  const base = pathname.split('?')[0]
  if (ROUTE_TO_SURFACE[base]) return ROUTE_TO_SURFACE[base]
  if (base.startsWith('/chat')) return 'chat'
  if (base.startsWith('/ai-assistant')) return 'ai_assistant'
  if (base.startsWith('/ai-avatar')) return 'ai_avatar'
  if (base.startsWith('/admin')) return 'admin'
  if (base.startsWith('/support')) return 'support'
  return 'unknown'
}

function scheduleFlush() {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    void flushEvents()
  }, FLUSH_INTERVAL_MS)
}

export function trackEvent(
  eventName: string,
  props: Record<string, unknown> = {},
  meta?: { surface?: string; entity_type?: string; entity_id?: string },
): void {
  const properties = {
    schema_version: ANALYTICS_SCHEMA_VERSION,
    client: 'web',
    ...props,
  }
  buffer.push({
    event_name: eventName,
    session_id: getAnalyticsSessionId(),
    surface: meta?.surface,
    entity_type: meta?.entity_type,
    entity_id: meta?.entity_id,
    properties,
  })
  if (buffer.length >= MAX_BATCH) {
    void flushEvents()
  } else {
    scheduleFlush()
  }
}

export function trackSectionView(route: string): void {
  trackEvent(
    'section_viewed',
    { route },
    { surface: routeToSurface(route) },
  )
}

export function trackSessionStarted(): void {
  getSessionStartedAt()
  trackEvent('session_started', {}, { surface: 'studio' })
}

export function trackSessionEnded(): void {
  const started = getSessionStartedAt()
  const durationSec = Math.max(0, Math.round((Date.now() - started) / 1000))
  trackEvent('session_ended', { duration_sec: durationSec })
}

export function trackUserError(message: string, context?: string): void {
  const truncated = message.slice(0, 200)
  trackEvent(
    'user_error_shown',
    {
      error_message_trunc: truncated,
      error_source: context || 'ui',
    },
    { surface: routeToSurface(window.location.pathname) },
  )
}

export function trackGenerationClientEvent(
  phase: 'started' | 'completed' | 'failed',
  kind: string,
  extra: Record<string, unknown> = {},
): void {
  const eventName =
    phase === 'started'
      ? 'generation_started'
      : phase === 'completed'
        ? 'generation_completed'
        : 'generation_failed'
  trackEvent(
    eventName,
    { generation_kind: kind, ...extra },
    { surface: routeToSurface(window.location.pathname) },
  )
}

export async function flushEvents(useBeacon = false): Promise<void> {
  if (flushing || buffer.length === 0) return
  flushing = true
  const batch = buffer.splice(0, MAX_BATCH)
  flushing = false

  const payload = { events: batch }

  if (useBeacon && typeof window !== 'undefined') {
    try {
      const { useAuthStore } = await import('../store/authStore')
      const token = useAuthStore.getState().accessToken
      const base = import.meta.env.VITE_API_URL || ''
      await fetch(`${base}/api/v1/analytics/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        keepalive: true,
      })
      return
    } catch {
      buffer.unshift(...batch)
      return
    }
  }

  try {
    await api.post('/analytics/events', payload)
  } catch {
    buffer.unshift(...batch)
  }
}

export function initAnalyticsLifecycle(): () => void {
  trackSessionStarted()

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      trackSessionEnded()
      void flushEvents(true)
    }
  }
  const onUnload = () => {
    trackSessionEnded()
    void flushEvents(true)
  }

  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('pagehide', onUnload)

  return () => {
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('pagehide', onUnload)
  }
}
